import { PriceType } from "@/types/types";

export enum AtsIndicator {
  N = 'N'
};

export enum Side {
  bid = 'bid',
  dealer = 'dealer',
  offer = 'offer'
};

export enum AlertTargetType {
  price =  'price',
  ytm =  'ytm', // yield to maturity
  spread =  'gspread' 
}

export type BaseInferenceRequest = {
  figi: string;
  ats_indicator: AtsIndicator;
  quantity: number;
  side: Side;
  rfq_label: PriceType;
};

export type InferenceRequestItem = BaseInferenceRequest & {
  subscribe?: boolean;
  timestamp?: string[];
  unsubscribe?: boolean;
};



export type ApiRequest = {
  reset?: boolean;
  trade?: {
    figi: string;
    index?: number[];
    subscribe?: boolean;
    unsubscribe?: boolean;
    /** Include inference in the response */
    include_inference?: boolean;
  }[]
  inference?: InferenceRequestItem[]
};

export type Inference = {
  ats_indicator: AtsIndicator;
  date: Date;
  figi: string;
  price?: number[];
  spread?: number[];
  ytm?: number[];
  quantity: number;
  side: Side;
  tenor: number;
  /** Treasury CUSIP available only for spread model */
  treasury_cusip?: string;
}

export type OldInferenceResponse = {
  ats_indicator: AtsIndicator;
  bid: number[];
  date: string;
  dealer: number[];
  figi: string;
  offer: number[];
  quantity: number;
  tenor: number;
  /** Treasury CUSIP available only for spread model */
  treasury_cusip?: string;
};

export type NewInferenceResponse = {
  ats_indicator: AtsIndicator;
  side: Side;
  date: string;
  figi: string;
  quantity: number;
  spread?: number[];
  ytm?: number[];
  price?: number[];
  tenor: number;
  /** Treasury CUSIP available only for spread model */
  treasury_cusip?: string;
};

export type InferenceResponse = OldInferenceResponse | NewInferenceResponse;

type TradeBase = {
  /**
   * The trade's identity: the server-composed collapse key (DMM-839 protocol §3).
   * Opaque -- never parse it, rebuild it or display it. Stable across a collapse, which changes a
   * trade's `quantity` and `report_date` but never its key, so it doubles as a UI row handle.
   */
  key: string,
  figi: string,
  /** A key column, so it is what the `N`/`Y` in `key` encodes -- not the `AtsIndicator` enum, which has no `Y`. */
  ats_indicator: 'Y' | 'N';
  /**
   * Display side, which folds `buy_sell` in: `(C,B)` and `(A,B)` both arrive as `B`, `(C,S)`/`(A,S)`/`(T,S)`
   * as `S`. Lossy by design -- the history keys on `side` and `buy_sell` separately, which is why the
   * key is composed server-side rather than rebuilt from these fields (DMM-839 protocol §3).
   */
  side: 'B' | 'D' | 'S';
  quantity: number;
  price: number;
  /** Always sent; null when it could not be computed. */
  yield: number | null;
  /** Only under `$.data.trades.calculate_ytm`; null when not computed. */
  ytm?: number | null;
  /** Basis points. Only under `$.data.trades.calculate_spread`; null when not computed. */
  spread?: number | null;
  /** Maturity bucket in years (2, 3, 5, 7, 10, 20, 30). Only under `$.data.trades.calculate_spread`. */
  tenor?: number | null;
  /**
   * Best-effort (DMM-839 protocol §7 rule 9): absent when inference failed for the batch, or when the
   * trade has no preceding transaction and so cannot be priced. Such a trade is delivered plain
   * rather than withheld -- under delta publishing, withholding it would hide it until its key next
   * changed, which for a trade that never collapses is never. Read these through `?.`.
   */
  inferred_price?: number[];
  /** Best-effort as above, and only when the spread model is loaded. */
  inferred_spread?: number[];
  /** Best-effort as above, and only when the spread model is loaded. */
  inferred_ytm?: number[];
}

export type Trade = TradeBase & {
  report_date: Date;
  execution_date: Date;
};

export type TradeResponse = TradeBase & {
  report_date: string;
  execution_date: string;
};

type WsApiMessageBase<T> = {
  message: string;
  description: string;
  data: T;
}

type Deactivated = {
  message: 'deactivated';
}

type UnrecognizedFigi = WsApiMessageBase<{
  unrecognized_figis: string[];
}>

export const isMessageResponse = (response: ApiResponse): response is (Deactivated | UnrecognizedFigi) =>
  response.hasOwnProperty('message');

type Data = {
  /** Created and changed trades only -- never untouched rows (DMM-839 protocol §6). Upsert by `key`. */
  trade?: TradeResponse[];
  /**
   * Keys to drop from the store. Reserved by the protocol and never sent today: a collapse needs no
   * deletion channel, so the only true removals would be FINRA cancels/corrections, which the live
   * path does not apply. An unknown key is a no-op.
   */
  deleted?: string[];
  inference?: InferenceResponse[];
}

export type ApiResponse = Deactivated | UnrecognizedFigi | Data;
